<template>
  <WdsStack direction-column gap-custom="2.5rem" class="tw-py-2">
    <BaseSection>
      <BaseSectionHeader :header-text="$t('Overview')" />

      <BaseSectionBody>
        <BaseFieldGroup :label="$t('Title')" :helper-text="$t('Shown to applicants')">
          <div class="tw-w-full">
            <WdsInput
              v-model="store.form.name"
              class="tw-w-full"
              :placeholder="$t('Senior Front-End Engineer')"
              data-test="job-title"
              required
              :error="getErrorMessage(validationStore, 'name')"
              @update:model-value="() => resetExternalValidationErrors('name')"
            />
          </div>
        </BaseFieldGroup>

        <BaseFieldGroup
          :label="$t('Description')"
          :helper-text="$t('Details about the role and responsibilities')"
          full-width
        >
          <template #label>
            <WdsStack direction-row class="tw-w-full tw-justify-between">
              <WdsText primary large>{{ $t('Description') }}</WdsText>

              <JobDescriptionGenerator
                v-if="aiConsent"
                :job-id="store.persistedJob?.id"
                :title="store.form.name"
                :description="store.form.description"
                @generate-description="handleGeneratedDescription"
              />
            </WdsStack>
            <WdsStack v-if="aiDescriptionUsed" direction-column class="tw-w-full tw-py-2">
              <WdsAlert warning icon>
                {{
                  $t(
                    'AI draft — please review for accuracy, fairness, and legal compliance before using.',
                  )
                }}
              </WdsAlert>
            </WdsStack>
          </template>
          <div class="tw-w-full">
            <WdsInputArea
              v-model="store.form.description"
              :placeholder="$t('Job description') + '...'"
              data-test="job-description"
              height="200px"
              required
              :error="getErrorMessage(validationStore, 'description')"
              @update:model-value="() => resetExternalValidationErrors('description')"
            />
          </div>
        </BaseFieldGroup>
      </BaseSectionBody>
    </BaseSection>

    <BaseSection v-show="fieldableStore.fields.length > 0">
      <BaseSectionHeader :header-text="$t('Custom fields')" />
      <BaseSectionBody>
        <FieldableFormCustomFields
          v-model:field-values="store.form.customFieldValues"
          :context-message="
            $t('Details specified below will only be visible to you and your team members.')
          "
          :fieldable-type="TypeSupportingCustomFieldValues.Job"
          :fieldable-id="store.persistedJob?.id"
          :account-id="currentAccountId"
          :should-display-errors="store.hasBeenTouched"
        />
      </BaseSectionBody>
    </BaseSection>

    <BaseSection>
      <WdsStack direction-row justify-space-between class="tw-w-full">
        <BaseSectionHeader :header-text="$t('Skills & Experience')" />
        <JobMetadataGenerator
          v-if="aiConsent"
          :job-id="store.persistedJob?.id"
          :title="store.form.name"
          :description="store.form.description"
          @generate-metadata="handleGeneratedMetadata"
        />
      </WdsStack>

      <BaseSectionBody>
        <BaseFieldGroup
          :label="$t('Seniority')"
          :helper-text="$t('What experience level is this job for?')"
        >
          <div class="tw-w-full">
            <WdsSelect
              v-model="selectedExperienceLevel"
              :list="experienceLevelOptions"
              :placeholder="$t('Choose experience level') + '...'"
              data-test="job-experience-level-dropdown"
              :error="getErrorMessage(validationStore, 'expectedExperienceLevel')"
              multi-select
            />
          </div>
        </BaseFieldGroup>

        <BaseFieldGroup :label="$t('Skills')" :helper-text="$t('Used to match candidate profiles')">
          <div class="tw-w-full">
            <TypeSelectSkills
              v-model="computedSkills"
              :skillable-type="[SkillableType.Job]"
              :error="getErrorMessage(validationStore, 'skills')"
              :show-type-suggestions="true"
            />
          </div>
        </BaseFieldGroup>

        <BaseFieldGroup
          :label="$t('Industry')"
          :helper-text="$t('Specific industry experience')"
          optional
        >
          <div class="tw-w-full">
            <TypeSelectIndustries
              v-model="computedIndustries"
              can-deselect
              :error="getErrorMessage(validationStore, 'industries')"
            />
          </div>
        </BaseFieldGroup>

        <BaseFieldGroup
          :label="$t('Required languages')"
          :helper-text="$t('Which languages are required for this job?')"
          optional
        >
          <div class="tw-w-full">
            <WdsSelect
              v-model="computedLanguages"
              multi-select
              :list="languageOptions"
              :placeholder="$t('Add a language')"
              data-test="job-language-dropdown"
              can-deselect
            />
          </div>
        </BaseFieldGroup>
        <BaseFieldGroup
          v-for="{ name } in store.form.languages"
          :key="name"
          :label="$t(convertEnumToDisplayText(name))"
          :helper-text="$t('What experience level is expected for this language?')"
          :data-test="`job-language-${name.toLowerCase()}-section`"
        >
          <div class="tw-w-full">
            <WdsSelect
              :placeholder="$t('Select experience level')"
              :list="[
                { value: Fluency.Good, label: $t(convertEnumToDisplayText(Fluency.Good)) },
                { value: Fluency.Fluent, label: $t(convertEnumToDisplayText(Fluency.Fluent)) },
              ]"
              :model-value="getLanguageExperience(name)"
              :data-test="`job-language-${name.toLowerCase()}-experience-dropdown`"
              @update:model-value="(value) => updateLanguageExperience(value, name)"
            />
          </div>
        </BaseFieldGroup>
      </BaseSectionBody>
    </BaseSection>

    <BaseSection>
      <BaseSectionHeader :header-text="$t('Documents')" />
      <BaseSectionBody>
        <JobAttachments v-model:attachments="store.form.attachments" />
      </BaseSectionBody>
    </BaseSection>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  WdsAlert,
  WdsInput,
  WdsInputArea,
  WdsSelect,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'

import { useTranslation } from '@/composables/translation'
import BaseSection from '@/components/BaseSection.vue'
import BaseSectionHeader from '@/components/BaseSectionHeader.vue'
import BaseSectionBody from '@/components/BaseSectionBody.vue'
import BaseFieldGroup from '@/components/BaseFieldGroup.vue'
import FieldableFormCustomFields from '@/components/Fieldable/FieldableFormCustomFields.vue'
import TypeSelectSkills from '@/components/TypeSuggestion/TypeSelectSkills.vue'
import TypeSelectIndustries from '@/components/TypeSuggestion/TypeSelectIndustries.vue'
import {
  ExpectedExperienceLevel,
  Fluency,
  LanguageName,
  TypeSupportingCustomFieldValues,
  SkillableType,
} from '@/gql/graphql'
import { convertEnumToDisplayText } from '@/utils/tools'

import { getErrorMessage } from '@/components/Job/functions/getErrorMessage'
import { useJobFormStore } from '@/components/Job/Edit/store/jobFormStore'
import { useValidationStore } from '@/components/Job/Edit/store/useValidationStore'
import JobAttachments from '@/components/Job/Edit/Tabs/OverviewTab/JobAttachments.vue'
import { useFieldableFormStore } from '@/components/Fieldable/store/useFieldableFormStore'
import JobDescriptionGenerator from '@/components/Job/Edit/Tabs/OverviewTab/JobDescriptionGenerator.vue'
import JobMetadataGenerator, {
  GeneratedJobMetadataResponse,
} from '@/components/Job/Edit/Tabs/OverviewTab/JobMetadataGenerator.vue'

const currentAccountId = ref(currentAccount.globalId)
const store = useJobFormStore()
const fieldableStore = useFieldableFormStore()
const validationStore = useValidationStore()
const { $t } = useTranslation()

const aiConsent = computed(() => currentAccount?.settings?.ai_consent)

const aiDescriptionUsed = ref(false)

const resetExternalValidationErrors = (name: string) => {
  validationStore.$externalResults[name] = undefined
}

const computedSkills = computed({
  get: () => store.form.skills.map((skillName) => ({ label: skillName, value: skillName })),
  set: (skills) => {
    store.form.skills = skills.map(({ value }) => value)
  },
})

const computedIndustries = computed({
  get: () => store.form.industries.map(({ id, name }) => ({ value: id, label: name })),
  set: (industries) => {
    store.form.industries = industries.map(({ label, value }) => ({ id: value, name: label }))
  },
})

const computedLanguages = computed({
  get: () => store.form.languages.map(({ name }) => ({ value: name })),
  set: (selectedLanguages) => {
    // If a language has been removed
    if (selectedLanguages.length < store.form.languages.length) {
      const deletedLanguage = store.form.languages.find(
        (language) => !selectedLanguages.some(({ value }) => value === language.name),
      )

      store.form.languages = store.form.languages.filter(
        (language) => language.name !== deletedLanguage?.name,
      )
    }

    // If a language has been added
    else {
      const languagesMap = store.form.languages.reduce(
        (accumulator, currentLanguage) => ({
          ...accumulator,
          [currentLanguage.name]: currentLanguage.experience,
        }),
        {},
      )
      store.form.languages = selectedLanguages.map(({ value: languageName }) => ({
        name: languageName,
        experience: languagesMap[languageName] ?? Fluency.Fluent,
      }))
    }
  },
})

const getLanguageExperience = (languageName) => ({
  value: store.form.languages.find((language) => language.name === languageName)?.experience,
})

const updateLanguageExperience = (experience, languageName) => {
  const languageToUpdate = store.form.languages.find((language) => language.name === languageName)

  if (languageToUpdate) {
    languageToUpdate.experience = experience.value
  }
}

const selectedExperienceLevel = computed({
  get: () => store.form.expectedExperienceLevel.map((experience) => ({ value: experience })),
  set: (selected) => {
    store.form.expectedExperienceLevel = selected.map((experience) => experience.value)
  },
})

const experienceLevelOptions = [
  { value: ExpectedExperienceLevel.Student, label: $t('Student') },
  { value: ExpectedExperienceLevel.Junior, label: $t('Junior') },
  { value: ExpectedExperienceLevel.Senior, label: $t('Senior') },
  { value: ExpectedExperienceLevel.Expert, label: $t('Expert') },
]

const languageOptions = Object.values(LanguageName).map((value) => ({
  value,
  label: convertEnumToDisplayText(value),
}))

const handleGeneratedDescription = (description: string) => {
  store.form.description = description
  aiDescriptionUsed.value = true
}

const handleGeneratedMetadata = (response: GeneratedJobMetadataResponse) => {
  // Auto-fill skills
  if (response.skills?.length) {
    store.form.skills = response.skills
  }

  // Auto-fill experience level
  if (response.seniority?.length) {
    store.form.expectedExperienceLevel = response.seniority
  }

  // Auto-fill industries if industry is provided
  if (response.industries?.length) {
    // Assuming industry is a string ID or name that needs to be converted to the right format
    store.form.industries = response.industries
  }
}
</script>
